import { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { adminFinanceApi } from '@/api/adminFinance'
import { formatMoney } from '@/components/billing/formatMoney'
import { formatDateDMY } from '@/utils/date'
import { getErrorMessage } from '@/utils/error'
import {
  PAYMENT_STATUS_FILTERS,
  type AdminPayment,
  type PaymentStatusFilter,
} from '@/types/adminFinance'

type PaymentAction = 'refund' | 'markFailed' | 'markSucceeded'

export function PaymentsTab() {
  const { t, i18n } = useTranslation()
  const adapterLocale = i18n.language === 'ru' ? 'ru' : 'en'

  const [rows, setRows] = useState<AdminPayment[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(15)
  const [status, setStatus] = useState<PaymentStatusFilter | ''>('')
  const [from, setFrom] = useState<Dayjs | null>(null)
  const [to, setTo] = useState<Dayjs | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuRow, setMenuRow] = useState<AdminPayment | null>(null)

  const [actionTarget, setActionTarget] = useState<{ payment: AdminPayment; action: PaymentAction } | null>(null)
  const [reason, setReason] = useState('')
  // Спрашиваем об отмене подписки только когда она есть и ещё активна
  const [cancelSubscription, setCancelSubscription] = useState(false)
  const [processing, setProcessing] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await adminFinanceApi.listPayments({
        page,
        per_page: perPage,
        ...(status ? { status } : {}),
        ...(from ? { from: from.format('YYYY-MM-DD') } : {}),
        ...(to ? { to: to.format('YYYY-MM-DD') } : {}),
      })
      setRows(result.data)
      setTotal(result.total)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [page, perPage, status, from, to, t])

  useEffect(() => {
    void load()
  }, [load])

  const runAction = async () => {
    if (!actionTarget) return
    setProcessing(true)
    setError(null)
    try {
      if (actionTarget.action === 'refund') {
        await adminFinanceApi.refundPayment(
          actionTarget.payment.id,
          reason || undefined,
          cancelSubscription,
        )
      } else if (actionTarget.action === 'markFailed') {
        await adminFinanceApi.markPaymentFailed(actionTarget.payment.id)
      } else {
        await adminFinanceApi.markPaymentSucceeded(actionTarget.payment.id)
      }
      setActionTarget(null)
      setReason('')
      setCancelSubscription(false)
      await load()
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setProcessing(false)
    }
  }

  const renderStatus = (payment: AdminPayment) => {
    if (payment.is_refunded) {
      return <Chip size="small" color="info" label={t('admin.finance.statuses.refunded', 'Возврат')} />
    }
    const color = payment.status === 'paid' ? 'success' : payment.status === 'rejected' ? 'error' : 'warning'
    return <Chip size="small" color={color} label={t(`admin.finance.statuses.${payment.status}`, payment.status)} />
  }

  const actionLabel = (action: PaymentAction): string =>
    action === 'refund'
      ? t('admin.finance.actions.refund', 'Возврат')
      : action === 'markFailed'
        ? t('admin.finance.actions.markFailed', 'Отклонить')
        : t('admin.finance.actions.markSucceeded', 'Отметить оплаченным')

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ sm: 'center' }}>
        <TextField
          select
          size="small"
          label={t('admin.finance.status', 'Статус')}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as PaymentStatusFilter | '')
            setPage(1)
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">{t('admin.finance.allStatuses', 'Все статусы')}</MenuItem>
          {PAYMENT_STATUS_FILTERS.map((s) => (
            <MenuItem key={s} value={s}>
              {t(`admin.finance.statuses.${s}`, s)}
            </MenuItem>
          ))}
        </TextField>
        <DatePicker
          label={t('admin.finance.from', 'С')}
          value={from}
          onChange={(d) => {
            setFrom(d)
            setPage(1)
          }}
          slotProps={{ textField: { size: 'small' } }}
        />
        <DatePicker
          label={t('admin.finance.to', 'По')}
          value={to}
          onChange={(d) => {
            setTo(d)
            setPage(1)
          }}
          slotProps={{ textField: { size: 'small' } }}
        />
      </Stack>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.finance.date', 'Дата')}</TableCell>
              <TableCell>{t('admin.finance.teacher', 'Учитель')}</TableCell>
              <TableCell>{t('admin.finance.plan', 'План')}</TableCell>
              <TableCell align="right">{t('admin.finance.amount', 'Сумма')}</TableCell>
              <TableCell>{t('admin.finance.method', 'Способ')}</TableCell>
              <TableCell>{t('admin.finance.status', 'Статус')}</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}>
                    <Skeleton height={32} />
                  </TableCell>
                </TableRow>
              ))
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  {t('admin.finance.empty', 'Записей нет')}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((payment) => (
                <TableRow key={payment.id} hover>
                  <TableCell>{payment.created_at ? formatDateDMY(payment.created_at) : '—'}</TableCell>
                  <TableCell>{payment.user.name || `#${payment.user.id}`}</TableCell>
                  <TableCell>{payment.plan.name || `#${payment.plan.id}`}</TableCell>
                  <TableCell align="right">{formatMoney(payment.amount)}</TableCell>
                  <TableCell>{t(`admin.finance.methods.${payment.method}`, payment.method)}</TableCell>
                  <TableCell>{renderStatus(payment)}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        setMenuAnchor(e.currentTarget)
                        setMenuRow(payment)
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={Math.max(0, page - 1)}
        onPageChange={(_, newPage) => setPage(newPage + 1)}
        rowsPerPage={perPage}
        onRowsPerPageChange={(e) => {
          setPerPage(parseInt(e.target.value, 10))
          setPage(1)
        }}
        rowsPerPageOptions={[10, 15, 25, 50]}
        labelRowsPerPage={t('admin.finance.rowsPerPage', 'Строк на странице:')}
        labelDisplayedRows={({ from: f, to: tt, count }) => `${f}–${tt} ${t('admin.finance.of', 'из')} ${count}`}
      />

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          disabled={!menuRow || menuRow.status !== 'paid' || menuRow.is_refunded}
          onClick={() => {
            if (menuRow) setActionTarget({ payment: menuRow, action: 'refund' })
            setMenuAnchor(null)
          }}
        >
          {t('admin.finance.actions.refund', 'Возврат')}
        </MenuItem>
        <MenuItem
          disabled={!menuRow || menuRow.status === 'rejected' || menuRow.is_refunded}
          onClick={() => {
            if (menuRow) setActionTarget({ payment: menuRow, action: 'markFailed' })
            setMenuAnchor(null)
          }}
        >
          {t('admin.finance.actions.markFailed', 'Отклонить')}
        </MenuItem>
        <MenuItem
          disabled={!menuRow || menuRow.status === 'paid' || menuRow.is_refunded}
          onClick={() => {
            if (menuRow) setActionTarget({ payment: menuRow, action: 'markSucceeded' })
            setMenuAnchor(null)
          }}
        >
          {t('admin.finance.actions.markSucceeded', 'Отметить оплаченным')}
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(actionTarget)} onClose={() => setActionTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>{actionTarget ? actionLabel(actionTarget.action) : ''}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t('admin.finance.confirmAction')}
            {actionTarget ? ` #${actionTarget.payment.id}` : ''}
          </Typography>
          {actionTarget?.action === 'refund' && (
            <>
              {actionTarget.payment.subscription?.is_active && (
                <FormControlLabel
                  sx={{ mb: 1, display: 'block' }}
                  control={
                    <Checkbox
                      checked={cancelSubscription}
                      onChange={(e) => setCancelSubscription(e.target.checked)}
                    />
                  }
                  label={t('admin.finance.cancelSubscriptionOnRefund')}
                />
              )}
              <TextField
                label={t('admin.finance.reason')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionTarget(null)} disabled={processing}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={runAction} disabled={processing}>
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  )
}
